import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserInfo } from '../../../state/resources/users/actions';
import Department, { DepartmentWithRole } from '../entities/departments/model';
import { Role } from '../entities/roles/role.model';
import { setDepartment } from '../../../utils/auth';

export interface AuthUser {
  uuid: string;
  firstName: string;
  lastName: string;
  email1: string;
  email2: null;
  office_phone: null;
  mobile_phone: null;
  companyUuid: string;
  EIN: null;
  driverCDLExpiration: null;
  driverCDL: null;
  numberOfImpersonations: number;
  twic: null;
  suspendedUntil: null;
  one_time_password: false;
  hasTwic: false;
  deviceType: string;
  pushNotificationToken: null;
  zendeskUserId: null;
  authProviderId: string;
  createdAt: string;
  updatedAt: string;
  departments: DepartmentWithRole[];
}

export const fetchUserInfoAction = createAsyncThunk(
  'auth/fetchUserInfo',
  async (
    _,
    { rejectWithValue }
  ): Promise<
    Omit<AuthUser, 'departments'> & {
      departments: Department[];
      roles: Role[];
    }
  > => {
    try {
      const { data } = await fetchUserInfo();

      return data;
    } catch (error) {
      return rejectWithValue(error.message) as any;
    }
  }
);

interface AuthSliceState {
  user: AuthUser | null;
  fetchingAuthUser: boolean;
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    /**
     * Default to true so we don't incorrectly logout before trying to fetch user info
     */
    fetchingAuthUser: true
  } as AuthSliceState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserInfoAction.pending, (state) => {
      state.fetchingAuthUser = true;
    });
    builder.addCase(fetchUserInfoAction.fulfilled, (state, action) => {
      const { roles, ...rest } = action.payload;

      state.user = {
        ...rest,
        departments: rest.departments.map((dept) => {
          const role = roles.find((r) => r.UserRelation.departmentUuid === dept.uuid) ?? null;

          return {
            ...dept,
            role
          };
        })
      };

      setDepartment(state.user.departments[0] || null);

      state.fetchingAuthUser = false;
    });
    builder.addCase(fetchUserInfoAction.rejected, (state) => {
      state.fetchingAuthUser = false;
    });
  }
});

export const { setUser, clearUser } = authSlice.actions;

export default authSlice.reducer;
